import { useState, useCallback, useEffect, useMemo } from "react";
import { Box, Divider, Checkbox } from "@mui/material";
import styled from "@emotion/styled";
import { useRecoilValue } from "recoil";
import parse from "html-react-parser";

import { GLOBAL_CONST, FLOW, REWARD_OFFERING } from "../../constants";
import {
  replaceGlobalConfigVars,
  wireCallbacks,
  redirectLoaded,
  sendAnalytics,
  delay,
} from "../../utils";
import { appParamState } from "../../atoms";

import CompetitionDialog from "./CompetitionDialog";
import OnboardingStep from "./OnboardingStep";
import OnboardingContent from "./OnboardingContent";
import StickyCTA from "./OnboardingStickyCTA";
import Dropdown from "./Dropdown";
import DropdownContent from "./DropdownContent";
import SuccessAnimation from "./SuccessAnimation";
import { useCompetition } from "../hooks/useCompetition";

type ValidOfferIds = REWARD_OFFERING;

const HelpHeading = styled.div`
  text-align: left;
  width: 100%;
`;

const TermsCheckboxContainer = styled.div`
  margin-top: 2px;
  padding-bottom: 2px;
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled.span`
  font-weight: 400;
  font-size: 0.75rem !important;
  color: #2f383c;
`;

const GreenCheckbox = styled(Checkbox)({
  "&.MuiCheckbox-root": {
    color: "#0e893c",
  },
  "&.Mui-checked": {
    color: "#0e893c",
  },
});

const CircleUnchecked = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" stroke="#0e893c" strokeWidth="2" />
  </svg>
);

const CircleChecked = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="12" fill="#0e893c" />
    <path d="M9.5 16.2L5.8 12.5l-1.4 1.4 5.1 5.1 11-11-1.4-1.4z" fill="#fff" />
  </svg>
);

interface CompetitionPageProps {
  readonly offerId: ValidOfferIds;
  readonly additionalData?: Record<string, unknown>;
  readonly customContent?: React.ReactNode | ((hasEntered: boolean) => React.ReactNode);
  readonly loadingMessage?: string;
  readonly alreadyEnteredText?: string;
}

export default function CompetitionPage({
  offerId,
  additionalData,
  customContent,
  loadingMessage,
  alreadyEnteredText,
}: CompetitionPageProps) {
  const { flow } = useRecoilValue(appParamState);
  const globalPartnerCfg = GLOBAL_CFG[offerId];
  const competition = useCompetition(
    offerId, 
    additionalData
  );

  const resolvedCustomContent = typeof customContent === "function"
    ? customContent(competition.hasEntered)
    : customContent;

  const disclaimers = useMemo(() => {
    const content = globalPartnerCfg.onboarding1.content ?? "";
    const matches = Array.from(
      (content as string).matchAll(
        /<p id="terms-checkbox-text".*?>(.*?)<\/p>/g
      )
    );
    return matches.map((m) => m[1]);
  }, [globalPartnerCfg]);

  const [checkedStates, setCheckedStates] = useState(
    disclaimers.map(() => false)
  );
  const allChecked = checkedStates.every(Boolean);

  const transformContent = useCallback((content: string) => {
    return content.replace(
      /<p id="terms-checkbox-text".*?>(.*?)<\/p>/g,
      "<div class='terms-marker'></div>"
    );
  }, []);

  const processedContent = transformContent(
    replaceGlobalConfigVars(globalPartnerCfg.onboarding1.content ?? "")
  );

  const [stickyHeight, setStickyHeight] = useState(0);
  const basePadding = GLOBAL_CONST.PLATFORM === "android" ? 320 : 200;
  const contentPaddingBottom = Math.max(basePadding, stickyHeight + 32);

  const handleStickyHeightChange = useCallback((height: number) => {
    setStickyHeight(height);
  }, []);

  const handleCheckboxChange = useCallback(
    (index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const next = [...checkedStates];
      next[index] = evt.target.checked;
      setCheckedStates(next);
    },
    [checkedStates]
  );

  const handleCompetitionEntry = useCallback(async () => {
    if (!allChecked) return;
    await sendAnalytics("competitionEntryButtonClick");
    await delay(100);
    await competition.enterCompetition();
  }, [allChecked, competition]);

  useEffect(() => {
    if (competition.initialLoading) return;
    wireCallbacks();
    if (flow === FLOW.ONBOARDING || flow === FLOW.REDEEM) {
      redirectLoaded();
      sendAnalytics("competitionScreenOnLoad");
    }
  }, [flow, competition.initialLoading]);

  if (competition.initialLoading) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Box
        sx={{
          flex: 1,
          padding: "24px",
          paddingBottom: `${contentPaddingBottom}px`,
        }}
      >
        <OnboardingStep
          image={globalPartnerCfg.onboarding1.image}
          imageLottie={globalPartnerCfg.onboarding1.lottie}
        >
          <OnboardingContent
            iconUrl={globalPartnerCfg.onboarding1.pointIcon}
          >
            {parse(processedContent)}

            {resolvedCustomContent}

            {disclaimers.map((text, i) => (
              <TermsCheckboxContainer key={i}>
                <GreenCheckbox
                  checked={checkedStates[i]}
                  onChange={handleCheckboxChange(i)}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleChecked />}
                />
                <CheckboxLabel>
                  {parse(replaceGlobalConfigVars(text))}
                </CheckboxLabel>
              </TermsCheckboxContainer>
            ))}

            {disclaimers.length > 0 && <Box sx={{ height: "16px" }} />}

            {renderDropdowns(globalPartnerCfg)}
          </OnboardingContent>
        </OnboardingStep>

        <SuccessAnimation
          show={competition.entrySuccess && !competition.loading}
          animationPath="/custom/common/party.json"
          autoHide
          autoHideDelay={3000}
        />
      </Box>

      <StickyCTA
        legalContent={parse(
          replaceGlobalConfigVars(globalPartnerCfg.onboarding1.legal ?? "")
        )}
        ctaText={competition.hasEntered ? (alreadyEnteredText || "Already Entered") : (globalPartnerCfg.onboarding1.ctaText ?? "Enter Now")}
        ctaAction={handleCompetitionEntry}
        disabled={competition.hasEntered || !allChecked}
        onHeightChange={handleStickyHeightChange}
      />

      <CompetitionDialog
        open={competition.loading || competition.dialogOpen}
        loading={competition.loading}
        success={competition.entrySuccess}
        onClose={competition.closeDialog}
        loadingMessage={loadingMessage || "Entering competition..."}
        successMessage={globalPartnerCfg.onboarding1.successMessage}
        errorMessage={globalPartnerCfg.onboarding1.errorMessage}
      />
    </Box>
  );
}

function renderDropdowns(partnerConfig: any) {
  if (!partnerConfig.onboarding1.dropdownHeading) return null;
  return (
    <>
      <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
      <HelpHeading>
        {parse(
          replaceGlobalConfigVars(partnerConfig.onboarding1.dropdownHeading)
        )}
      </HelpHeading>
      {(partnerConfig.onboarding1.dropdowns ?? []).map(
        (dd: any, idx: number) => (
          <Dropdown
            key={idx}
            items={[
              parse(replaceGlobalConfigVars(dd.heading)),
              <DropdownContent
                content={dd.content}
                iconUrl={partnerConfig.onboarding1.pointIcon}
              />,
            ]}
          />
        )
      )}
    </>
  );
}